/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useRef } from 'react';
import { 
  Building, Trash2, ExternalLink,
  Sparkles, Image as ImageIcon,
  Check, Copy, PlusCircle, ToggleLeft, ToggleRight, Crown, Upload,
  Eye, LogOut, User, Package
} from 'lucide-react';
import { Vendor, Product } from '../types';
import { supabase } from '../lib/supabase';

interface DashboardProps {
  initialVendor?: Vendor;
  onSelectVendor: (vendor: Vendor) => void;
  onBackToHome: () => void;
  onSignOut: () => void;
}

const IMAGE_PRESETS = [
  { name: "Kente Shawl", url: "https://images.unsplash.com/photo-1544816155-12df9643f363?q=80&w=600&auto=format&fit=crop" },
  { name: "Handmade Beads", url: "https://images.unsplash.com/photo-1611591437281-460bfbe1220a?q=80&w=600&auto=format&fit=crop" },
  { name: "Whipped Shea Butter", url: "https://images.unsplash.com/photo-1608248597279-f99d160bfcbc?q=80&w=600&auto=format&fit=crop" },
  { name: "Black Soap Brand", url: "https://images.unsplash.com/photo-1601049541289-9b1b7bbbfe19?q=80&w=600&auto=format&fit=crop" },
  { name: "Woven Basket Bag", url: "https://images.unsplash.com/photo-1590874103328-eac38a683ce7?q=80&w=600&auto=format&fit=crop" },
  { name: "Leather Shoes", url: "https://images.unsplash.com/photo-1562273138-f46be4ebdf33?q=80&w=600&auto=format&fit=crop" }
];export default function Dashboard({ initialVendor, onSelectVendor, onBackToHome, onSignOut }: DashboardProps) {
  const [vendor, setVendor] = useState<Vendor | null>(initialVendor || null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // New Product Form State
  const [newTitle, setNewTitle] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [newStock, setNewStock] = useState('10');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // General dashboard stats
  const [copiedLink, setCopiedLink] = useState(false);
  // Mobile tab state
  const [mobileTab, setMobileTab] = useState<'add' | 'products' | 'account'>('add');

  useEffect(() => {
    if (vendor) {
      fetchProducts();
    }
  }, [vendor]);

  async function fetchProducts() {
    if (!vendor) return;
    setLoading(true);
    try {
      const { data: vendorData } = await supabase
        .from('vendors').select('*').eq('business_name', vendor.business_name).single();
      const { data: productsData } = await supabase
        .from('products').select('*').eq('vendor_id', vendor.id).order('created_at', { ascending: false });

      if (productsData) setProducts(productsData);
      if (vendorData) { setVendor(vendorData); onSelectVendor(vendorData); }
    } catch (err) {
      console.error('Error loading vendor catalog items:', err);
    } finally {
      setLoading(false);
    }
  }

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif'];
    if (!allowed.includes(file.type)) {
      setFormError('Please upload a JPG, PNG, WebP, or GIF image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setFormError('Image must be under 5MB.');
      return;
    }
    setFormError(null);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vendor) return;
    setFormError(null);
    setFormSuccess(false);

    if (!newTitle.trim()) { setFormError('Product title is required.'); return; }
    const parsedPrice = parseFloat(newPrice);
    if (isNaN(parsedPrice) || parsedPrice < 0) { setFormError('Price must be a positive number.'); return; }
    const parsedStock = parseInt(newStock);
    if (isNaN(parsedStock) || parsedStock < 0) { setFormError('Stock quantity cannot be negative.'); return; }
    if (!imageFile) { setFormError('Please upload a product image.'); return; }

    try {
      // 1. Upload image to Supabase Storage
      setUploadingImage(true);
      const ext = imageFile.name.split('.').pop() || 'jpg';
      const filePath = `${vendor.id}/${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(filePath, imageFile, { contentType: imageFile.type, upsert: false });

      if (uploadError) throw new Error(`Image upload failed: ${uploadError.message}`);

      // 2. Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('product-images')
        .getPublicUrl(filePath);

      setUploadingImage(false);

      // 3. Save product via Edge Function
      const fnUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/products`;
      const response = await fetch(fnUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          vendor_id: vendor.id,
          title: newTitle,
          price: parsedPrice,
          stock_count: parsedStock,
          image_url: publicUrl,
          is_available: parsedStock > 0,
        }),
      });

      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Failed to add product');

      setFormSuccess(true);
      setNewTitle(''); setNewPrice(''); setNewStock('10');
      setImageFile(null); setImagePreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchProducts();
      setTimeout(() => setFormSuccess(false), 3000);
    } catch (err: any) {
      setUploadingImage(false);
      setFormError(err.message || 'An error occurred.');
    }
  };

  const handleToggleAvailability = async (product: Product) => {
    const originalAvailable = product.is_available;
    const targetCount = originalAvailable ? 0 : (product.stock_count === 0 ? 5 : product.stock_count);

    setProducts(prev => prev.map(p => p.id === product.id
      ? { ...p, is_available: !originalAvailable, stock_count: targetCount } : p));

    try {
      const fnUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/products/${product.id}`;
      const res = await fetch(fnUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ is_available: !originalAvailable, stock_count: targetCount }),
      });
      if (!res.ok) throw new Error('Could not update product');
      const updated = await res.json();
      setProducts(prev => prev.map(p => p.id === product.id ? updated : p));
    } catch {
      fetchProducts();
    }
  };

  const handleDeleteProduct = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    setProducts(prev => prev.filter(p => p.id !== id));
    try {
      const fnUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/products/${id}`;
      await fetch(fnUrl, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` },
      });
    } catch {
      fetchProducts();
    }
  };

  const copyUrl = () => {
    if (!vendor) return;
    const fullUrl = `${window.location.origin}/${vendor.business_name}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  if (!vendor) return null;

  return (
    <div className="min-h-screen bg-transparent text-slate-900 pb-20 font-sans">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/70 backdrop-blur-md border-b border-white/60 px-4 py-3">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-2">
          {/* Left — VendorPulse brand */}
          <span className="font-display font-black text-base text-slate-900 tracking-tight shrink-0">
            Vendor<span className="text-orange-500">Pulse</span>
          </span>

          {/* Right — actions */}
          <div className="flex items-center gap-2">
            {/* Home */}
            <button
              onClick={onBackToHome}
              className="px-3 py-1.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-xs font-semibold text-slate-600 whitespace-nowrap transition-all"
            >
              Home
            </button>

            {/* Live View */}
            <a
              href={`/${vendor.business_name}`}
              target="_blank"
              rel="noreferrer"
              className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5"
            >
              <Eye className="w-3.5 h-3.5 shrink-0" />
              <span>Live View</span>
            </a>

            {/* Logout */}
            <button
              onClick={onSignOut}
              className="px-3 py-1.5 rounded-xl border border-red-200 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5"
            >
              <LogOut className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 mt-6 pb-24">

        {/* ── Mobile tab bar ─────────────────────────────────────────────── */}
        <div className="flex lg:hidden bg-white/60 backdrop-blur-md rounded-2xl p-1 mb-5 border border-white/60 shadow-sm">
          {([
            { id: 'add',      label: 'Add Product', icon: <PlusCircle className="w-4 h-4" /> },
            { id: 'products', label: 'Products',    icon: <Package className="w-4 h-4" /> },
            { id: 'account',  label: 'Account',     icon: <User className="w-4 h-4" /> },
          ] as const).map(tab => (
            <button
              key={tab.id}
              onClick={() => setMobileTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold transition-all ${
                mobileTab === tab.id
                  ? 'bg-white shadow-sm text-slate-900'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.icon}
              <span className="hidden xs:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* ── Desktop 3-col / Mobile tabbed ──────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* LEFT — Account card + Upgrade */}
          <div className={`lg:col-span-1 space-y-4 ${mobileTab !== 'account' ? 'hidden lg:block' : ''}`}>
            {/* Vendor Details */}
            <div className="bg-white/40 backdrop-blur-lg border border-white/60 rounded-3xl p-5 shadow-xl">
              <div className="flex justify-between items-start">
                <div>
                  <span className="text-[10px] uppercase font-bold tracking-wider text-slate-400 block mb-1">Business Card</span>
                  <h3 className="font-display font-bold text-xl text-slate-900 capitalize break-all">{vendor.business_name}</h3>
                  <p className="text-xs text-slate-500 mt-1">{vendor.email}</p>
                  <p className="text-xs text-slate-500 mt-0.5 font-mono">{vendor.whatsapp_number}</p>
                </div>
                {vendor.subscription_status === 'premium' ? (
                  <div className="flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full text-xs font-bold border border-emerald-100">
                    <Crown className="w-3.5 h-3.5 shrink-0 fill-emerald-100" />
                    <span>PREMIUM</span>
                  </div>
                ) : (
                  <div className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full text-xs font-bold border border-slate-200">FREE</div>
                )}
              </div>

              {/* Catalog link */}
              <div className="mt-5 pt-5 border-t border-slate-100">
                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block mb-2">Your Catalog Link</span>
                <div className="flex gap-2">
                  <input
                    type="text" readOnly
                    value={`${window.location.origin}/${vendor.business_name}`}
                    className="flex-1 bg-white/60 px-3 py-1.5 rounded-xl text-xs font-semibold font-mono border border-slate-200 focus:outline-none text-slate-700"
                  />
                  <button onClick={copyUrl} className="p-2 border border-slate-200 rounded-xl hover:bg-slate-100 transition-colors shrink-0 text-slate-500">
                    {copiedLink ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Upgrade card */}
            {vendor.subscription_status === 'premium' ? (
              <div className="bg-slate-900 rounded-3xl p-5 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/20 rounded-full blur-2xl pointer-events-none" />
                <div className="relative z-10 flex items-center gap-2 mb-2">
                  <Crown className="w-5 h-5 text-amber-400" />
                  <h4 className="font-display font-semibold text-white text-sm">Premium Active</h4>
                </div>
                <p className="text-slate-400 text-xs leading-relaxed relative z-10">Unlimited products unlocked.</p>
              </div>
            ) : (
              <div className="bg-slate-900 rounded-3xl p-5 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/20 rounded-full blur-2xl pointer-events-none" />
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-2">
                    <Crown className="w-5 h-5 text-amber-400" />
                    <h4 className="font-display font-semibold text-white text-sm">Upgrade to Premium</h4>
                  </div>
                  <p className="text-slate-400 text-xs leading-relaxed mb-4">
                    Free tier is limited to 4 products. Upgrade for <span className="text-white font-semibold">₵25/month</span> to unlock unlimited uploads.
                  </p>
                  <button
                    className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 active:scale-95 text-white font-semibold text-xs rounded-xl transition-all shadow-lg shadow-orange-500/30"
                    onClick={() => {/* Paystack — coming soon */}}
                  >
                    Upgrade Now — ₵25/mo
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* MIDDLE — Add product form */}
          <div className={`lg:col-span-1 ${mobileTab !== 'add' ? 'hidden lg:block' : ''}`}>
            <div className="bg-white/40 backdrop-blur-lg border border-white/60 rounded-3xl p-5 shadow-xl lg:sticky lg:top-20">
              <div className="flex items-center gap-2 mb-4">
                <PlusCircle className="w-5 h-5 text-amber-500" />
                <h3 className="font-display font-semibold text-base">Add Catalogue Item</h3>
              </div>

              <form onSubmit={handleAddProduct} className="space-y-4">
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400 block mb-1.5">Item Title</label>
                  <input
                    type="text" placeholder="e.g. Handmade Shea Scented Soap"
                    value={newTitle} onChange={e => setNewTitle(e.target.value)}
                    className="w-full bg-white/60 border border-slate-200 rounded-xl px-3.5 py-2 text-sm text-slate-800 hover:bg-white focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400 block mb-1.5">Price (GHS)</label>
                    <div className="relative">
                      <span className="absolute left-3.5 top-2.5 text-xs text-slate-400 font-bold">₵</span>
                      <input
                        type="number" placeholder="75" min="0" step="0.01"
                        value={newPrice} onChange={e => setNewPrice(e.target.value)}
                        className="w-full bg-white/60 border border-slate-200 rounded-xl pl-8 pr-3 py-2 text-sm text-slate-800 hover:bg-white focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400 block mb-1.5">Stock</label>
                    <input
                      type="number" placeholder="10" min="0"
                      value={newStock} onChange={e => setNewStock(e.target.value)}
                      className="w-full bg-white/60 border border-slate-200 rounded-xl px-3.5 py-2 text-sm text-slate-800 hover:bg-white focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all"
                      required
                    />
                  </div>
                </div>

                {/* Image upload */}
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400 block mb-1.5">Product Photo</label>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className={`relative w-full aspect-video rounded-xl border-2 border-dashed cursor-pointer overflow-hidden transition-all ${
                      imagePreview ? 'border-orange-400' : 'border-slate-200 hover:border-orange-400 bg-white/40'
                    }`}
                  >
                    {imagePreview ? (
                      <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-slate-400">
                        <Upload className="w-6 h-6" />
                        <span className="text-[11px] font-semibold">Tap to upload photo</span>
                        <span className="text-[9px]">JPG, PNG, WebP · max 5MB</span>
                      </div>
                    )}
                    {uploadingImage && (
                      <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                        <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/avif" onChange={handleImageChange} className="hidden" />
                  {imageFile && (
                    <button type="button"
                      onClick={() => { setImageFile(null); setImagePreview(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                      className="mt-1.5 text-[10px] text-slate-400 hover:text-red-500 transition-colors"
                    >✕ Remove photo</button>
                  )}
                </div>

                {formError && <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-red-700 text-xs">⚠️ {formError}</div>}
                {formSuccess && <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-emerald-800 text-xs">🎉 Product added to your catalog!</div>}

                <button type="submit"
                  className="w-full py-3 bg-orange-500 hover:bg-orange-600 active:scale-95 text-white font-semibold text-xs tracking-wide uppercase rounded-xl transition-all shadow-lg shadow-orange-500/20"
                >
                  Add to Catalog
                </button>
              </form>
            </div>
          </div>

          {/* RIGHT — Live products list */}
          <div className={`lg:col-span-1 space-y-3 ${mobileTab !== 'products' ? 'hidden lg:block' : ''}`}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display font-semibold text-sm uppercase tracking-wider text-slate-500">
                Live Products ({products.length})
              </h3>
              {vendor.subscription_status === 'free' && (
                <span className="text-[10px] text-slate-400 font-bold bg-slate-100 px-2 py-0.5 rounded-full">
                  {products.length} / 4
                </span>
              )}
            </div>

            {products.length === 0 ? (
              <div className="bg-white/40 backdrop-blur-lg border border-white/60 rounded-3xl p-8 text-center shadow-xl">
                <ImageIcon className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="font-medium text-slate-500 text-sm">No products yet.</p>
                <p className="text-xs text-slate-400 mt-1">
                  {window.innerWidth < 1024 ? 'Tap "Add Product" to get started.' : 'Use the form to add your first item.'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {products.map(item => {
                  const isSoldOut = !item.is_available || item.stock_count === 0;
                  return (
                    <div key={item.id} className="bg-white/40 backdrop-blur-lg border border-white/60 shadow-lg p-3 flex gap-3 hover:border-orange-400 transition-colors rounded-2xl">
                      <div className="relative w-14 h-14 rounded-xl bg-slate-50 overflow-hidden shrink-0">
                        <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                        {isSoldOut && (
                          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                            <span className="text-[7px] font-bold text-white uppercase border border-white/40 px-1 rounded">Out</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col justify-between">
                        <div>
                          <h4 className="font-medium text-slate-900 text-xs line-clamp-1 capitalize">{item.title}</h4>
                          <p className="text-xs font-bold text-slate-900">₵ {item.price.toLocaleString('en-GH', { minimumFractionDigits: 2 })}</p>
                          <p className="text-[10px] text-slate-400 mt-0.5">{item.stock_count} left</p>
                        </div>
                        <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-slate-50">
                          <button onClick={() => handleToggleAvailability(item)} className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-900 transition-colors">
                            {item.is_available && item.stock_count > 0 ? (
                              <><ToggleRight className="w-4 h-4 text-emerald-600" /><span className="text-emerald-700 text-[10px] font-bold">Active</span></>
                            ) : (
                              <><ToggleLeft className="w-4 h-4 text-slate-400" /><span className="text-slate-400 text-[10px] font-bold">Sold Out</span></>
                            )}
                          </button>
                          <button onClick={() => handleDeleteProduct(item.id)} className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
